import { getAccessToken } from '@/config/api/authStore.js'
import {
  getApiBaseUrl,
  getAuthHeaderName,
  getAuthHeaderPrefix,
  isCredentialsRequired,
} from '@/config/api/env.js'
import { chatStreamHeaders } from './chatStreamRequest'

export class ClarificationSubmitError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'ClarificationSubmitError'
    this.status = status
  }
}

function url(assistantMessageId) {
  const base = getApiBaseUrl()
  const path = `/v1/chat/clarifications/${encodeURIComponent(assistantMessageId)}/submit/stream`
  if (!base) return path
  return base.endsWith('/v1') ? `${base}${path.slice(3)}` : `${base}${path}`
}

/**
 * Submit one card and return the open SSE response.
 *
 * The endpoint validates and commits before it writes a single stream byte, so
 * this promise resolving *is* the acceptance signal — which is exactly what the
 * generic send path could not give the card. A `422` or `409` arrives here as a
 * status the caller can act on, not as an error event buried inside a `200`.
 */
export async function postClarificationStream({ assistantMessageId, answers }, signal) {
  const res = await fetch(url(assistantMessageId), {
    method: 'POST',
    signal,
    headers: chatStreamHeaders({
      token: getAccessToken(),
      headerName: getAuthHeaderName(),
      headerPrefix: getAuthHeaderPrefix(),
    }),
    credentials: isCredentialsRequired() ? 'include' : 'same-origin',
    body: JSON.stringify({ answers }),
  })
  if (!res.ok || !res.body) {
    throw new ClarificationSubmitError(await detailOf(res), res.status)
  }
  return res
}

// FastAPI reports the reason as `{ "detail": … }`, and for a 422 that reason is
// written for the user ("that is not a recognised timezone"). Showing the raw
// JSON body instead would waste the only useful part of the response.
async function detailOf(res) {
  try {
    const body = await res.json()
    const detail = body?.detail
    if (typeof detail === 'string' && detail) return detail
  } catch {
    /* not JSON */
  }
  return `Could not submit those answers (${res.status})`
}
